import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { keyframes } from '@emotion/react'

// Light sweep over the "generating" label so it reads as active, not static.
const shimmer = keyframes({
  from: { backgroundPosition: '200% 0' },
  to: { backgroundPosition: '-200% 0' },
})

export const useStyles = makeStyles()((theme: Theme) => ({
  wrapper: {
    position: 'relative',
    width: '100%',
  },
  // "Generating next best actions" affordance shown in the chip slot. Reserves
  // roughly the chips' two-row footprint (chip 28px × 2 + 12px gap) and centers
  // the row within it, so the indicator sits vertically centered in the same
  // space the chips occupy instead of hugging the input.
  generatingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    minHeight: 0,
  },
  generatingIcon: {
    fontSize: 14,
    lineHeight: 2,
    // Part of the brand AI gradient (labraAIGradient.middle) — left as a literal
    // to keep the gradient family untouched per request.
    color: '#bf548e',
    flexShrink: 0,
  },
  generatingText: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontStyle: 'italic',
    fontSize: 14,
    lineHeight: 2,
    // Shimmer uses the input box's border gradient (#ff708a → #bf548e →
    // #733495). Symmetric stops so the sweep loops seamlessly.
    background:
      'linear-gradient(90deg, #ff708a, #bf548e, #733495, #bf548e, #ff708a)',
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: `${shimmer} 1.8s linear infinite`,
  },
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  // Single-row horizontal slider used once a conversation has started.
  // `safe center` centers the chips when they fit (e.g. full-screen's wide
  // width) but falls back to start-alignment when they overflow, so the slider
  // stays scrollable and the first chip is never clipped (compact drawer).
  rootSlider: {
    flexWrap: 'nowrap',
    justifyContent: 'safe center',
    overflowX: 'auto',
    // overflow-x:auto forces overflow-y to compute as auto, which would clip
    // the chip's bottom hairline border. A little vertical padding keeps the
    // chips (and their ::after border) clear of the clip edge.
    padding: '2px 0',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  chip: {
    position: 'relative',
    flexShrink: 0,
    backgroundColor: theme.palette.text.white,
    border: 'none',
    borderRadius: 20,
    padding: '4px 12px',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: theme.palette.labraText.tertiary,
    whiteSpace: 'nowrap',
    transition: 'background-color 0.15s ease',
    // Figma border is 0.5px solid #000. On a 1x display a 0.5px border rounds
    // up to a solid 1px line, so render a true half-pixel hairline via a 2x
    // overlay scaled to 50%.
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '200%',
      height: '200%',
      border: `1px solid ${theme.palette.black.dark}`,
      borderRadius: 40,
      transform: 'scale(0.5)',
      transformOrigin: 'top left',
      pointerEvents: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette.datePicker.light,
    },
    '&.selected': {
      backgroundColor: theme.palette.labraChat.hoverBgAlt,
    },
  },
  // Chevron affordances shown only in slider mode, with a fade so chips
  // don't cut off abruptly behind them.
  scrollArrow: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 40,
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: 14,
    color: theme.palette.labraText.tertiary,
    zIndex: 1,
  },
  scrollArrowLeft: {
    left: 0,
    justifyContent: 'flex-start',
    background: 'linear-gradient(to left, rgba(255,255,255,0), #ffffff 60%)',
  },
  scrollArrowRight: {
    right: 0,
    justifyContent: 'flex-end',
    background: 'linear-gradient(to right, rgba(255,255,255,0), #ffffff 60%)',
  },
}))
